import React, { useEffect, useState } from "react";
import { decryptData } from "../../utils/crypto";
import { ChevronLeft, Plus, Shop, Trash3 } from "react-bootstrap-icons";
import { useNavigate, useParams } from "react-router-dom";
import {
  createProducts,
  getProductsDetails,
  updateProducts,
} from "../../services/productServices";
import Utils from "../../utils";
import {
  getAllUnitsList,
  getAllCategoryList,
} from "../../services/commonServices";
import { useTranslation } from "react-i18next";
import Select from "react-select";
import { customStyles } from "../../utils/function";
import { notify } from "../../components/Common/Alerts";
import { useConfig } from "../../components/Context/ConfigContext";

const AddProduct = () => {
  const navigate = useNavigate();
  const { uuid } = useParams();
  const { t } = useTranslation();

  const [formData, setFormData] = useState({
    name: "",
    sku: "",
    description: "",
    unit_id: "",
    entity_type: "",
    status: 1,
    uuid: "",
    unit_prices: [
      {
        unit_id: "",
        selling_price: "",
        unit_qty: "",
        cost_price: "",
        default_unit: 1,
      },
    ],
    category_id: "",
  });
  const [errors, setErrors] = useState({
    name: "",
    sku: "",
    unit_id: "",
    entity_type: "",
    category_id: "",
    unit_prices: [],
  });
  const [btnLoading, setBtnLoading] = useState(false);
  const [storeDetails, setStoreDetails] = useState("");
  const [unitsList, setUnitsList] = useState([]);
  const [selectedUnitData, setSelectedUnitData] = useState("");
  const [categoryList, setCategoryList] = useState([]);

  const unitOptions = unitsList || [];
  const { configData } = useConfig();

  useEffect(function () {
    getAllUnitsList()
      .then((response) => {
        let decrypted = decryptData(response.data.data);
        let list = [];
        for (let i = 0; i < decrypted?.data?.length; i++) {
          list.push({
            value: decrypted.data[i].id,
            label:
              decrypted.data[i].name +
              " (" +
              decrypted.data[i].short_name +
              ")",
            short_name: decrypted.data[i].short_name,
            min_value: decrypted.data[i].min_value,
          });
        }
        setUnitsList(list);
      })

      .catch((err) => {
        notify.error(Utils.getErrorMessage(err));
      });
  }, []);

  useEffect(function () {
    getAllCategoryList()
      .then((response) => {
        let decrypted = decryptData(response.data.data);
        let list = [];
        for (let i = 0; i < decrypted?.data?.length; i++) {
          list.push({
            value: decrypted.data[i].id,
            label: decrypted.data[i].name,
          });
        }
        setCategoryList(list);
      })

      .catch((err) => {
        notify.error(Utils.getErrorMessage(err));
      });
  }, []);

  const validateField = (name, value) => {
    switch (name) {
      case "name":
        if (!value) return "Name is required";
        return "";
      case "sku":
        if (!value) return t("skuIsRequired");
        if (/\s/.test(value)) return t("skuCannotSpaces");
        return "";

      case "category_id":
        if (!value) return t("categoryIsRequired");
        return "";

      default:
        return "";
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;

    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    setErrors((prev) => ({
      ...prev,
      [name]: validateField(name, value),
    }));
  };

  useEffect(
    function () {
      if (uuid) {
        let data = {
          uuid: uuid,
        };
        getProductsDetails(data)
          .then((response) => {
            const decrypted = decryptData(response.data.data);
            setStoreDetails(decrypted.data);
          })
          .catch((err) => {
            notify.error(Utils.getErrorMessage(err));
          });
      }
    },
    [navigate, uuid],
  );

  useEffect(
    function () {
      if (storeDetails) {
        setFormData({
          name: storeDetails?.name,
          sku: storeDetails?.sku,
          description: storeDetails?.description,
          unit_id: storeDetails?.unit_id,
          entity_type: storeDetails?.entity_type,
          status: storeDetails?.status,
          unit_prices: storeDetails?.unit_prices,
          uuid: storeDetails?.uuid,
          category_id: storeDetails?.category_id,
        });
      }
    },
    [storeDetails],
  );

  const handleSubmit = async (e) => {
    e.preventDefault();

    const newErrors = {
      name: validateField("name", formData.name),
      sku: validateField("sku", formData.sku),
      category_id: validateField("category_id", formData.category_id),
    };

    if (formData?.unit_prices?.length === 0) {
      notify.error(t("atLeastOneUnitRequired"));
      return;
    }

    const unitErrors = formData.unit_prices.map((item) => {
      const errors = {};

      if (!item.unit_id) {
        errors.unit_id = t("unitIsRequired");
      }

      if (!item.unit_qty) {
        errors.unit_qty = t("unitQtyRequired");
      }

      if (!item.selling_price) {
        errors.selling_price = t("priceIsRequired");
      }
      if (!item.cost_price) {
        errors.cost_price = t("wholesalePriceIsRequired");
      }

      return errors;
    });

    Utils.focusError();
    const hasUnitErrors = unitErrors.some((err) => Object.keys(err).length > 0);

    if (hasUnitErrors) {
      newErrors.unit_prices = unitErrors;
      setErrors(newErrors);
      return;
    }

    setErrors(newErrors);
    const hasErrors = Object.values(newErrors).some((err) => err);
    if (hasErrors) return;

    if (!newErrors.name) {
      setBtnLoading(true);
      const payload = {
        name: formData.name?.trim(),
        sku: formData.sku,
        description: formData.description,
        status: formData.status,
        category_id: formData.category_id,
        unit_prices: formData.unit_prices,

        ...(formData?.uuid && { uuid: formData.uuid }),
      };
      const apiCall = formData?.uuid ? updateProducts : createProducts;

      try {
        const response = await apiCall(payload);
        const decrypted = decryptData(response.data.data);
        notify.success(decrypted.message);
        setFormData({
          name: "",
          sku: "",
          description: "",
          unit_id: "",
          entity_type: "",
          status: 0,
          uuid: "",
          unit_prices: [],
        });
        setErrors("");
        navigate("/products");
      } catch (err) {
        notify.error(Utils.getErrorMessage(err));
      } finally {
        setBtnLoading(false);
      }
    }
  };

  const addUnitRow = () => {
    setFormData((prev) => ({
      ...prev,
      unit_prices: [
        ...prev.unit_prices,
        {
          unit_id: "",
          selling_price: "",
          cost_price: "",
          unit_qty: "",
          default_unit: 0,
        },
      ],
    }));
  };

  const handleUnitChange = (index, field, value) => {
    const updated = [...formData.unit_prices];
    updated[index][field] = value;

    setFormData((prev) => ({
      ...prev,
      unit_prices: updated,
    }));

    if (field === "unit_qty") {
      const minVal = selectedUnitData[index]?.min_value || 1;
      if (!value || Number(value) < minVal) {
        setErrors((prev) => ({
          ...prev,
          unit_prices: {
            ...prev.unit_prices,
            [index]: {
              ...prev.unit_prices?.[index],
              unit_qty: `quantity must be at least ${minVal}`,
            },
          },
        }));
      } else {
        // Clear error
        setErrors((prev) => {
          const updatedErrors = { ...prev.unit_prices };
          if (updatedErrors?.[index]) delete updatedErrors[index].selling_price;
          return { ...prev, unit_prices: updatedErrors };
        });
      }
    }
  };

  const removeUnitRow = (index) => {
    if (formData.unit_prices.length === 1) {
      notify.error(t("atLeastOneUnitRequired"));
      return;
    }

    const updated = [...formData.unit_prices];
    const removedItem = updated[index];

    updated.splice(index, 1);

    if (removedItem?.default_unit === 1 && updated.length > 0) {
      updated[0].default_unit = 1;
    }

    setFormData((prev) => ({
      ...prev,
      unit_prices: updated,
    }));
  };

  const handleDefaultChange = (index) => {
    setFormData((prev) => {
      const updated = prev.unit_prices.map((item, i) => {
        return {
          ...item,
          default_unit: i === index ? 1 : 0,
        };
      });

      return {
        ...prev,
        unit_prices: updated,
      };
    });
  };

  return (
    <div className="w-full px-4 md:px-6 pb-10">
      {/* --- HEADER --- */}
      <div className="hidden md:flex flex items-center gap-4 md:gap-6 flex-shrink-0 px-2 mb-2">
        <button
          onClick={() => navigate(-1)}
          className="md:hidden group flex items-center justify-center w-10 h-10 md:w-auto md:h-auto md:px-4 md:py-2.5 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-700 transition-all active:scale-95 shadow-sm"
        >
          <ChevronLeft
            size={18}
            className="group-hover:-translate-x-1 transition-transform md:hidden"
          />
          <span className="hidden md:block ml-2 font-bold text-sm">
            {t("back")}
          </span>
        </button>

        <div className="flex-1 hidden md:block">
          <h2 className="text-xl md:text-2xl font-black text-slate-800 dark:text-white tracking-tight leading-tight">
            {uuid ? t("updateProducts") : t("addNewProducts")}{" "}
          </h2>
        </div>
      </div>

      <section className="w-full bg-white dark:bg-slate-900 rounded-[45px] border border-slate-100 p-6 md:p-10 lg:p-14 shadow-sm">
        <div className="flex items-center justify-between mb-12">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-brand-orange/10 text-brand-orange rounded-2xl flex items-center justify-center">
              <Shop size={24} />
            </div>
            <h2 className="text-xl font-black text-slate-800">
              {t("productDetails")}{" "}
            </h2>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-6">
          {" "}
          <div className="flex flex-col gap-2">
            <label htmlFor="name" className="input-label">
              {t("name")} <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              name="name"
              placeholder={t("enterProductName")}
              className={`input-field ${errors.name ? "input-error" : ""}`}
              value={formData?.name || ""}
              onChange={handleChange}
              maxLength={50}
            />
            {errors.name && <span className="text-danger">{errors.name}</span>}
          </div>
          {/* Mobile Number */}
          <div className="flex flex-col gap-2">
            <label htmlFor="sku" className="input-label">
              {t("sku")}
              <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              name="sku"
              placeholder={t("enterSKU")}
              className={`input-field ${errors.sku ? "input-error" : ""}`}
              value={formData?.sku || ""}
              onChange={handleChange}
              maxLength={10}
            />
            {errors.sku && <span className="text-danger">{errors.sku}</span>}
          </div>
          <div className="flex flex-col gap-2 md:col-span-2">
            <label htmlFor="description" className="input-label">
              {t("description")}
            </label>
            <textarea
              name="description"
              placeholder={t("enterYourDescription")}
              rows={3}
              className={`input-field ${errors.description ? "input-error" : ""}`}
              value={formData?.description || ""}
              onChange={handleChange}
            />
          </div>
          <div className="flex flex-col gap-2">
            <label htmlFor="unit_id" className="input-label">
              {t("productStatus")}
            </label>
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                className="sr-only peer"
                checked={formData.status === 1}
                onChange={(e) =>
                  setFormData((prev) => ({
                    ...prev,
                    status: e.target.checked ? 1 : 0,
                  }))
                }
              />
              <div className="w-14 h-7 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-brand-orange/20 rounded-full peer peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[4px] after:start-[4px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-brand-gradient shadow-inner"></div>
            </label>
          </div>
          <div className="flex flex-col gap-2">
            <label htmlFor="category_id" className="input-label">
              {t("category")}
              <span className="text-red-500">*</span>
            </label>
            <Select
              options={categoryList}
              value={
                categoryList.find(
                  (opt) => opt.value === formData.category_id,
                ) || null
              }
              onChange={(selected) =>
                setFormData((prev) => ({
                  ...prev,
                  category_id: selected?.value ?? "",
                }))
              }
              styles={customStyles}
              placeholder="Select category"
              isSearchable
              isClearable
            />
            {errors.category_id && (
              <span className="text-danger">{errors.category_id}</span>
            )}
          </div>
          <div className="col-span-full mt-8 bg-slate-50/30 p-6 rounded-2xl border border-slate-100">
            <div className="px-6 py-5 border-b border-slate-200 bg-gradient-to-r from-orange-50 to-white flex items-center justify-between">
              <div>
                <h3 className="text-lg font-bold text-slate-800">
                  Unit Prices
                </h3>

                <p className="text-sm text-slate-500 mt-1">
                  Add multiple unit prices for this product
                </p>
              </div>

              <button
                type="button"
                onClick={addUnitRow}
                className="hidden md:flex items-center gap-2 bg-[#E67E22] hover:bg-orange-600 text-white px-5 py-2.5 rounded-xl text-sm font-semibold shadow transition"
              >
                <Plus size={16} />
                Add Unit
              </button>
            </div>

            <div className="hidden lg:grid grid-cols-[2.2fr_1.3fr_1.3fr_110px_90px_60px] gap-4 px-6 py-4 bg-slate-50 border-b border-slate-200">
              <div className="text-xs font-semibold uppercase tracking-wider text-slate-500">
                Unit
              </div>

              <div className="text-xs font-semibold uppercase tracking-wider text-slate-500">
                Wholesale
              </div>

              <div className="text-xs font-semibold uppercase tracking-wider text-slate-500">
                Selling
              </div>

              <div className="text-xs text-center font-semibold uppercase tracking-wider text-slate-500">
                Qty
              </div>

              <div className="text-xs text-center font-semibold uppercase tracking-wider text-slate-500">
                Default
              </div>

              <div></div>
            </div>

            <div className="divide-y divide-slate-100">
              {formData.unit_prices.map((item, index) => (
                <div
                  key={index}
                  className="p-5 lg:p-6 bg-white lg:grid lg:grid-cols-[2.2fr_1.3fr_1.3fr_110px_90px_60px] gap-4 items-start space-y-4 lg:space-y-0 hover:bg-orange-50/30 transition"
                >
                  <div className="w-full">
                    <label className="md:hidden text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1 block">
                      Unit Type
                    </label>
                    <Select
                      options={unitOptions}
                      value={unitOptions.find((u) => u.value === item.unit_id)}
                      onChange={(selected) => {
                        handleUnitChange(index, "unit_id", selected?.value);

                        setSelectedUnitData((prev) => ({
                          ...prev,
                          [index]: selected,
                        }));
                      }}
                      styles={{
                        ...customStyles,
                        control: (base, state) => ({
                          ...base,
                          borderRadius: "12px",
                          padding: "4px",
                          border: state.isFocused
                            ? "1px solid #E67E22"
                            : "1px solid #f1f5f9",
                          backgroundColor: "#f8fafc",
                          boxShadow: state.isFocused
                            ? "0 0 0 4px rgba(230, 126, 34, 0.05)"
                            : "none",
                          transition: "all 0.2s",
                        }),
                      }}
                      placeholder="Select unit"
                    />
                    {errors.unit_prices?.[index]?.unit_id && (
                      <span className="text-[10px] text-red-500 mt-1 font-bold italic ml-2 block">
                        {errors.unit_prices[index].unit_id}
                      </span>
                    )}
                  </div>

                  <div className="w-full">
                    <label className="md:hidden text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1 block">
                      {t("wholesalePrice")}
                    </label>
                    <div className="relative group">
                      <span className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 font-bold text-sm group-focus-within:text-[#E67E22] transition-colors">
                        {configData?.COUNTRY_CURRENCY}
                      </span>
                      <input
                        type="number"
                        className="w-full pl-9 pr-4 py-3 rounded-xl border border-slate-100 bg-slate-50/50 outline-none focus:bg-white focus:border-[#E67E22] focus:ring-4 focus:ring-orange-50 transition-all text-sm font-bold text-slate-700"
                        value={item.cost_price}
                        onChange={(e) =>
                          handleUnitChange(index, "cost_price", e.target.value)
                        }
                        onKeyDown={(e) =>
                          ["e", "E", "+", "-"].includes(e.key) &&
                          e.preventDefault()
                        }
                        min={1}
                        placeholder={t("wholesalePrice")}
                      />
                    </div>

                    {errors.unit_prices?.[index]?.cost_price && (
                      <span className="text-[10px] text-red-500 mt-1 font-bold italic ml-2 block">
                        {errors.unit_prices[index].cost_price}
                      </span>
                    )}
                  </div>

                  <div className="w-full">
                    <label className="md:hidden text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1 block">
                      {t("price")}
                    </label>
                    <div className="relative group">
                      <span className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 font-bold text-sm group-focus-within:text-[#E67E22] transition-colors">
                        {configData?.COUNTRY_CURRENCY}
                      </span>
                      <input
                        type="number"
                        className="w-full pl-9 pr-4 py-3 rounded-xl border border-slate-100 bg-slate-50/50 outline-none focus:bg-white focus:border-[#E67E22] focus:ring-4 focus:ring-orange-50 transition-all text-sm font-bold text-slate-700"
                        value={item.selling_price}
                        onChange={(e) =>
                          handleUnitChange(
                            index,
                            "selling_price",
                            e.target.value,
                          )
                        }
                        onKeyDown={(e) =>
                          ["e", "E", "+", "-"].includes(e.key) &&
                          e.preventDefault()
                        }
                        min={1}
                        placeholder={t("price")}
                      />
                    </div>

                    {errors.unit_prices?.[index]?.selling_price && (
                      <span className="text-[10px] text-red-500 mt-1 font-bold italic ml-2 block">
                        {errors.unit_prices[index].selling_price}
                      </span>
                    )}
                  </div>

                  <div className="w-full">
                    <input
                      type="number"
                      className="w-full px-4 py-3 rounded-xl border border-slate-100 bg-slate-50/50 outline-none focus:bg-white focus:border-[#E67E22] focus:ring-4 focus:ring-orange-50 transition-all text-sm font-bold text-slate-700"
                      value={item.unit_qty}
                      onChange={(e) =>
                        handleUnitChange(index, "unit_qty", e.target.value)
                      }
                      onKeyDown={(e) =>
                        ["e", "E", "+", "-"].includes(e.key) &&
                        e.preventDefault()
                      }
                      min={selectedUnitData[index]?.min_value || 1}
                      placeholder={`Quantity ${selectedUnitData[index]?.min_value || ""}`}
                    />
                    {errors.unit_prices?.[index]?.unit_qty && (
                      <span className="text-[10px] text-red-500 mt-1 font-bold italic ml-2 block">
                        {errors.unit_prices[index].unit_qty}
                      </span>
                    )}
                  </div>

                  <div className="flex flex-col items-center justify-center pt-1 md:pt-0">
                    <label className="md:hidden text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2 block text-center">
                      {t("default")}
                    </label>
                    <label className="relative inline-flex items-center cursor-pointer group">
                      <input
                        type="checkbox"
                        className="sr-only peer"
                        checked={Number(item.default_unit) === 1}
                        onChange={() => handleDefaultChange(index)}
                      />
                      <div className="w-11 h-6 bg-slate-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:bg-[#E67E22] after:content-[''] after:absolute after:top-[4px] after:left-[4px] after:bg-white after:rounded-full after:h-4 after:w-4 after:transition-all after:shadow-sm"></div>
                    </label>
                  </div>

                  <div className="flex justify-end md:justify-center items-center pt-1 md:pt-0">
                    <button
                      type="button"
                      onClick={() => removeUnitRow(index)}
                      className="w-10 h-10 flex items-center justify-center text-slate-300 hover:text-red-500 hover:bg-red-50 rounded-xl transition-all border border-transparent hover:border-red-100"
                      title="Remove Unit"
                    >
                      <Trash3 size={18} />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="col-span-full flex items-end justify-end gap-4 mb-20 lg:mb-0 mt-4">
          {" "}
          <button
            className="btn-filter-close"
            onClick={() => {
              navigate("/products");
            }}
          >
            {t("back")}
          </button>
          <button
            type="button"
            form="category-form"
            className="btn-submit-orange flex items-center gap-2 mb-1"
            onClick={handleSubmit}
            disabled={btnLoading}
          >
            {btnLoading && (
              <span className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></span>
            )}
            {btnLoading
              ? t("submitting")
              : formData.uuid
                ? t("update")
                : t("submit")}
          </button>
        </div>
      </section>
    </div>
  );
};

export default AddProduct;
